// VENDORED COPY of frontend/src/lib/financial/aggregation.ts — do not edit here; keep in sync with the main app.
/**
 * Financial aggregation functions.
 *
 * Ported from static/apps/lib/financial-calc.js to eliminate the
 * window.AppLib dependency. Logic is an exact mirror of the legacy
 * calculateHorizonByCategory, summarizeByYear, and getCumulativeValues.
 */

import type {
  FinancialItem,
  HorizonItem,
  CategoryHorizonResult,
  YearSummary,
} from './types';
import { calculateItemHorizon } from './projections';
import type { HorizonOptions } from './projections';
import { getDefaultStartDate } from './date-utils';

/**
 * Calculate horizon grouped by category.
 *
 * @param items - Array of items with a category derivable via getCategoryFn
 * @param getCategoryFn - Function to extract a category label from each item
 * @param options - Calculation options
 * @returns Horizon results broken down by category
 */
export function calculateHorizonByCategory<T extends HorizonItem | FinancialItem>(
  items: T[],
  getCategoryFn: (item: T) => string,
  options: HorizonOptions = {},
): CategoryHorizonResult {
  const {
    horizonMonths = 36,
    startDate: startDateInput,
  } = options;

  const startDate =
    startDateInput instanceof Date
      ? startDateInput
      : startDateInput
        ? new Date(startDateInput)
        : getDefaultStartDate();

  const byCategory: CategoryHorizonResult['byCategory'] = {};
  const monthly: number[] = new Array(horizonMonths).fill(0);

  items.forEach(item => {
    const category = getCategoryFn(item) || 'Uncategorized';
    const values = calculateItemHorizon(item, { horizonMonths, startDate });

    // Initialise category bucket if needed
    if (!byCategory[category]) {
      byCategory[category] = {
        monthly: new Array(horizonMonths).fill(0),
        total: 0,
        items: [],
      };
    }

    // Aggregate
    values.forEach((value, index) => {
      byCategory[category].monthly[index] += value;
      monthly[index] += value;
    });

    byCategory[category].total += values.reduce((sum, v) => sum + v, 0);
    byCategory[category].items.push({ item: item as unknown as HorizonItem, values });
  });

  const total = monthly.reduce((sum, val) => sum + val, 0);

  return {
    byCategory,
    monthly,
    total,
  };
}

/**
 * Summarise a monthly values array into year buckets.
 *
 * @param monthly - Monthly values array
 * @param options - Options with optional startDate
 * @returns Array of year summaries ordered chronologically
 */
export function summarizeByYear(
  monthly: number[],
  options: { startDate?: Date | string } = {},
): YearSummary[] {
  const startDateInput = options.startDate;
  const startDate =
    startDateInput instanceof Date
      ? startDateInput
      : startDateInput
        ? new Date(startDateInput)
        : getDefaultStartDate();

  const byYear: Record<number, YearSummary> = {};
  const date = new Date(startDate);

  monthly.forEach((value) => {
    const year = date.getFullYear();

    if (!byYear[year]) {
      byYear[year] = { year, total: 0, months: [] };
    }

    byYear[year].months.push(value);
    byYear[year].total += value;

    date.setMonth(date.getMonth() + 1);
  });

  return Object.values(byYear);
}

/**
 * Convert a monthly values array into cumulative (running total) values.
 *
 * @param monthly - Monthly values
 * @returns Cumulative values array of the same length
 */
export function getCumulativeValues(monthly: number[]): number[] {
  let cumulative = 0;
  return monthly.map(value => {
    cumulative += value;
    return cumulative;
  });
}
